import { ListingItem, ListingType } from '@smg-real-estate/hg-search-libs';

import {
  convertListingTypesToValuesForTracking,
  convertListingTypeToValuesForTracking,
} from '@/helpers/GTM/GTMHelper';
import {
  GTMPageType,
  InteractionElement,
  InteractionEvent,
  InteractionGoal,
  ListName,
} from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { listingIdsFrom } from '@/helpers/ListingData/ListingData';

interface RecommendationsTrackingOptions {
  gtmPageType: GTMPageType;
  visibleItems?: number;
  activeIndex?: number;
  isNewConstruction?: boolean;
  changeInteraction?: InteractionElement;
  model?: string;
  listerId?: string;
}

interface RecommendationsClickedTrackingOptions extends RecommendationsTrackingOptions {
  selectedIndex: number;
}

interface BasicRecommendationTrackingParameters {
  activeIndex: number;
  visibleItems: number;
  allIds: string[];
  listingTypes: (ListingType | undefined)[];
  allTypes: string[];
  visibleIds: string[];
  listName: string;
}

const getBasicRecommendationTrackingParameters = (
  items: ListingItem[],
  options: RecommendationsTrackingOptions,
): BasicRecommendationTrackingParameters => {
  const allIds = listingIdsFrom(items);
  const activeIndex = options.activeIndex ?? 0;
  const visibleItems = options.visibleItems ?? 0;
  const listingTypes = items.map((item) => item.listingType?.type);

  return {
    activeIndex,
    visibleItems,
    allIds,
    listingTypes,
    allTypes: convertListingTypesToValuesForTracking(listingTypes),
    visibleIds: allIds.slice(activeIndex, activeIndex + visibleItems),
    listName: options?.isNewConstruction
      ? ListName.NEWCONSTRUCTION_RECOMMENDATION
      : ListName.RECOMMENDATION,
  };
};

export function trackRecommendationsLoaded(
  items: ListingItem[],
  options: RecommendationsTrackingOptions,
): void {
  const { visibleIds, listName, allIds, allTypes } = getBasicRecommendationTrackingParameters(
    items,
    options,
  );

  trackEventHarmonized(InteractionEvent.LIST_LOAD, {
    h_event_category_006: InteractionEvent.LIST_LOAD_RECOMMENDATION,
    h_event_action_007: options.gtmPageType,
    h_model_algo_142: options.model,
    h_event_label_008: `${visibleIds.length}_${items.length}`,
    h_list_name_086: listName,
    h_list_items_total_284: items.length,
    h_list_items_loaded_285: visibleIds.length,
    h_list_items_all_087: allIds.toString(),
    h_list_items_loaded_088: visibleIds.toString(),
    h_list_items_vas_089: allTypes.toString(),
    h_listing_project_id_159: options.listerId,
  });
}

export function trackRecommendationClicked(
  items: ListingItem[],
  options: RecommendationsClickedTrackingOptions,
): void {
  const { allIds, visibleIds, allTypes, listName } = getBasicRecommendationTrackingParameters(
    items,
    options,
  );

  // Get the clicked item
  const clickedItem: ListingItem = items[options.selectedIndex];
  const { listingType } = clickedItem;
  const { id: listingId } = clickedItem.listing!;

  trackEventHarmonized(InteractionEvent.LIST_CLICK_THROUGH, {
    h_event_category_006: InteractionGoal.LIST_CLICKTHROUGH_RECOMMENDATION,
    h_event_action_007: options.gtmPageType,
    h_event_label_008: listingId!,
    h_list_item_clicked_id_085: listingId!,
    h_list_name_086: listName,
    h_list_items_all_087: allIds.toString(),
    h_list_items_loaded_088: visibleIds.toString(),
    h_list_items_vas_089: allTypes.toString(),
    h_list_item_vas_090: convertListingTypeToValuesForTracking(listingType?.type),
    h_model_algo_142: options.model,
    h_position_on_list_overall_283: options.selectedIndex + 1,
    h_list_items_total_284: items.length,
    h_list_items_loaded_285: visibleIds.length,
    h_listing_project_id_159: options.listerId,
  });
}

export function trackRecommendationsChanged(
  items: ListingItem[],
  options: RecommendationsTrackingOptions,
): void {
  const { allIds, allTypes, listName, visibleIds } = getBasicRecommendationTrackingParameters(
    items,
    options,
  );

  trackEventHarmonized(InteractionEvent.LIST_CHANGE, {
    h_event_category_006: InteractionGoal.LIST_CHANGE_RECOMMENDATION,
    h_event_action_007: InteractionGoal.RECOMMENDATIONS_SHOW_MORE_ITEMS,
    h_event_label_008: options.changeInteraction,
    h_list_name_086: listName,
    h_list_items_all_087: allIds.toString(),
    h_list_items_loaded_088: visibleIds.toString(),
    h_list_items_vas_089: allTypes.toString(),
    h_model_algo_142: options.model,
    h_list_items_total_284: items.length,
    h_list_items_loaded_285: visibleIds.length,
    h_listing_project_id_159: options.listerId,
  });
}

export function getInteractionElementOfRecommendationChange({
  oldIndex,
  newIndex,
  lastIndex,
}: {
  oldIndex: number;
  newIndex: number;
  lastIndex: number;
}): InteractionElement | undefined {
  if (oldIndex === newIndex) {
    return;
  }

  // Stand at the end, go one step forward (slider automatically jumps to the first item)
  if (oldIndex === lastIndex && newIndex === 0) {
    return InteractionElement.SLIDER_CHANGE_NEXT;
  }

  // If it is at the beginning, go one step back (slider automatically jumps to the last item)
  if (oldIndex === 0 && newIndex === lastIndex) {
    return InteractionElement.SLIDER_CHANGE_PREVIOUS;
  }

  return newIndex > oldIndex
    ? InteractionElement.SLIDER_CHANGE_NEXT
    : InteractionElement.SLIDER_CHANGE_PREVIOUS;
}
